import { memo } from 'react';

import cn from 'classnames';

import { OButton, OPopper } from '@src/shared';

import { formatTimeStamp } from '@src/utils';

import useQuestionReply from './useQuestionReply';

import { BiDotsHorizontalRounded } from 'react-icons/bi';

import { EAnswerType, IQuestionAnswer, IQuestionAssignee } from '@src/types';

import styles from './questionReply.module.scss';

interface IProps {
  answer: IQuestionAnswer;
  author: IQuestionAssignee;
  isLastReply?: boolean;
  initialAnswerType?: EAnswerType;
}

const QuestionReply = ({ answer, author, isLastReply, initialAnswerType }: IProps) => {
  const { isEditable, replyMenuItems } = useQuestionReply({ answer, author });
  const { text, timestamp, answerType, edited } = answer;

  // For thread replies, use the initial answer type if provided, otherwise use the answer's own type
  const actualAnswerType = initialAnswerType || answerType;
  const isPrivate = actualAnswerType === EAnswerType.PRIVATE;

  if (!answer.text) return null;

  return (
    <div className={styles.questionReplyContainer}>
      <div className={styles.threadLineContainer}>
        <div className={cn(styles.threadLine, styles.horizontalLine)} />
        {!isLastReply && <div className={cn(styles.threadLine)} />}
      </div>
      <div className={styles.replyContent}>
        <div className={styles.replyHeader}>
          <span className={styles.author}>{author.name ?? 'me'}</span>
          <span className={styles.time}>
            {edited ? '(edited) ' : ''}
            {formatTimeStamp(timestamp || new Date().toString())}
          </span>
        </div>
        <div className={styles.replayContentBox}>
          <div className={styles.contentHeader}>
            <span className={styles.replyLabel}>Replied {isPrivate ? 'privately' : 'publicly'}</span>
            {isEditable && (
              <div className={styles.replyOptions}>
                <OPopper
                  icon={<BiDotsHorizontalRounded size={24} className={styles.menuIcon} />}
                  classes={{ iconButton: styles.menuIcon }}
                  position="bottom-end"
                >
                  <div className={styles.menuWrapper}>
                    {replyMenuItems.map(({ label, onClick }) => (
                      <OButton key={label} text={label} className={styles.menuItem} onClick={onClick} />
                    ))}
                  </div>
                </OPopper>
              </div>
            )}
          </div>
          <div className={styles.replyText}>{text}</div>
        </div>
      </div>
    </div>
  );
};

export default memo(QuestionReply);
